import { handleResponseErrors } from '@restDataAccess/handleResponseErrors';
import { findIdInUrl, getValidId, type WithId } from '@restDataAccess/withId';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';

import { authorizationHeader } from '../../authorizationHeader.ts';
import { useClearCacheCallback } from '../cacheHooks.ts';
import { assertApiBaseForFetch } from '../getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from '../useGetApiUrl.ts';

type AuthApiDeleteById = {
  baseApiRoute: ApiNameType;
  cacheKeys: string[];
  headers?: HeadersInit;
  identifier?: string;
  route: string;
  returnJsonResult?: boolean;
};

export const useAuthApiDeleteById = <T extends WithId, V = void>({
  baseApiRoute,
  cacheKeys,
  headers,
  identifier,
  route,
  returnJsonResult = true,
}: AuthApiDeleteById) => {
  const auth = useAuth();
  const api = useGetApiUrl(baseApiRoute);
  const clearCacheCallback = useClearCacheCallback(cacheKeys);

  const deleteDevice = async (device: Partial<T> & WithId) => {
    try {
      const base = assertApiBaseForFetch(api);
      const id = getValidId(device);
      const idNameToReplace = findIdInUrl(route);
      if (!idNameToReplace) {
        throw new Error(`No id name found in route ${route}`);
      }
      const response = await fetch(`${base}${route.replace(`{${idNameToReplace}}`, id)}`, {
        method: 'DELETE',
        body: JSON.stringify(device),
        headers: {
          ...authorizationHeader(auth.user),
          'Content-Type': 'application/json',
          ...headers,
        },
      });

      return await handleResponseErrors<V>(response, identifier, returnJsonResult);
    } catch (error) {
      console.error(`Error removing device ${identifier}`, error);
      throw error;
    }
  };

  return useMutation({
    mutationFn: deleteDevice,
    onSuccess: () => clearCacheCallback(),
    onError: () => clearCacheCallback(),
  });
};
